/* Глобальное состояние витрины: пользователь, корзина, справочники. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api.js';
import { flyToCart, pulse } from '../hooks/useMotion.js';
import { useToast } from './ToastContext.jsx';
import { money } from '../lib/format.js';

const StoreContext = createContext(null);

const EMPTY_CART = {
  count: 0,
  items: [],
  subtotal: 0,
  discount: 0,
  total: 0,
  shipping: { code: 'cdek', title: 'СДЭК — пункт выдачи', price: 350, free: false, needsPvz: true },
  promo: null,
  freeShippingFrom: 15000,
  freeShippingLeft: 15000,
};

const EMPTY_CATALOG = {
  products: [],
  categories: [],
  shipping: [],
  payments: [],
  cdekCities: [],
  homeReviews: [],
  stats: { products: 0, rating: 5, reviews: 0, freeShippingFrom: 15000 },
};

/*
Начальные данные приходят с сервера.

При серверном рендеринге каталог уже отрисован в присланной разметке, и первый
рендер в браузере обязан совпасть с ней до последнего узла — иначе React
выбросит разметку и перерисует страницу заново, а вместе с ней и весь смысл
затеи. Поэтому браузер стартует ровно с тех же данных, что были у сервера,
и только потом дозапрашивает своё: пользователя и корзину.

Пользователь и корзина на сервере не рендерятся намеренно: они зависят от кук
покупателя, и страницу с ними нельзя было бы кешировать.
*/
export function StoreProvider({ children, initial = null }) {
  const toast = useToast();

  const [oneMerch, setOneMerch] = useState(Boolean(initial?.onemerch));

  // Товар, отрисованный сервером: страница берёт его как начальные данные,
  // иначе при оживлении она показала бы скелетон поверх готовой разметки
  const ssrProduct = initial?.product ?? null;

  /*
  Точка отсчёта для дат.

  Считать их от собственных часов нельзя: сервер и браузер стоят в разных зонах,
  а страница живёт в кеше. Берём метку, с которой рисовал сервер; без неё
  (переход внутри витрины) — текущее время.
  */
  const [renderedAt] = useState(() => initial?.now ?? Date.now());

  // Каталог уже есть — показывать скелетоны не нужно
  const [ready, setReady] = useState(Boolean(initial));
  const [failed, setFailed] = useState(null);
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(EMPTY_CART);
  const [catalog, setCatalog] = useState(
    initial
      ? {
          ...EMPTY_CATALOG,
          products: initial.products ?? [],
          categories: initial.categories ?? [],
          shipping: initial.shipping ?? [],
          payments: initial.payments ?? [],
          cdekCities: initial.cdekCities ?? [],
          homeReviews: initial.homeReviews ?? [],
          stats: initial.stats ?? EMPTY_CATALOG.stats,
        }
      : EMPTY_CATALOG,
  );

  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    api
      .bootstrap()
      .then((data) => {
        setUser(data.user ?? null);
        setCart(data.cart ?? EMPTY_CART);
        // Оформление уходит на чекаут OneMerch, когда интеграция включена
        setOneMerch(Boolean(data.onemerch));
        setCatalog({
          products: data.products ?? [],
          categories: data.categories ?? [],
          shipping: data.shipping ?? [],
          payments: data.payments ?? [],
          cdekCities: data.cdekCities ?? [],
          homeReviews: data.homeReviews ?? [],
          stats: data.stats ?? catalog.stats,
        });
      })
      .catch((err) => {
        setFailed(err.message);
        toast.error('Сервер недоступен', 'Запустите Go-бекенд: go run ./cmd/server');
      })
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCart = useCallback(async () => {
    const { cart: fresh } = await api.cart();
    setCart(fresh);
    return fresh;
  }, []);

  /** Положить товар в корзину: запрос, анимация полёта, тост. */
  const addToCart = useCallback(
    async ({ slug, size = null, qty = 1, sourceEl = null }) => {
      const flight = sourceEl ? flyToCart(sourceEl) : Promise.resolve();
      const { cart: fresh } = await api.cartAdd({ slug, size, qty });
      setCart(fresh);
      await flight;
      pulse(document.getElementById('cartBtn'));

      const item = fresh.items.find((i) => i.slug === slug && (!size || i.size === size));
      const suffix = item?.size && item.size !== 'One size' ? ` · ${item.size}` : '';
      toast.success('Добавлено в корзину', item ? `${item.name}${suffix} — ${money(fresh.total)} к оплате` : '');
      return fresh;
    },
    [toast],
  );

  const updateQty = useCallback(async (itemId, qty) => {
    const { cart: fresh } = await api.cartUpdate(itemId, qty);
    setCart(fresh);
    return fresh;
  }, []);

  const removeItem = useCallback(async (itemId) => {
    const { cart: fresh } = await api.cartRemove(itemId);
    setCart(fresh);
    return fresh;
  }, []);

  const applyPromo = useCallback(async (code) => {
    const { cart: fresh, promo } = await api.cartPromo(code);
    setCart(fresh);
    return promo;
  }, []);

  const setShipping = useCallback(async (code) => {
    const { cart: fresh } = await api.cartShipping(code);
    setCart(fresh);
    return fresh;
  }, []);

  const login = useCallback(async (payload) => {
    const res = await api.login(payload);
    setUser(res.user);
    setCart(res.cart);
    return res.user;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await api.register(payload);
    setUser(res.user);
    setCart(res.cart);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
    await refreshCart().catch(() => {});
  }, [refreshCart]);

  const value = useMemo(
    () => ({
      ready, failed, user, cart, catalog, oneMerch, ssrProduct, renderedAt,
      setUser, setCart, setCatalog,
      refreshCart, addToCart, updateQty, removeItem, applyPromo, setShipping,
      login, register, logout,
    }),
    [ready, failed, user, cart, catalog, oneMerch, ssrProduct, renderedAt, refreshCart, addToCart, updateQty, removeItem,
      applyPromo, setShipping, login, register, logout],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore должен вызываться внутри StoreProvider');
  return ctx;
}
